/**
 * Caché global singleton para toda la app.
 * Permite guardar y recuperar cualquier recurso por clave.
 * TTL global de 5 minutos.
 */

const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

class GlobalCache {
  constructor() { }
  private cache: Map<string, CacheEntry<any>> = new Map();
  private pending: Map<string, Promise<any>> = new Map(); // promesas en curso

  /** Obtiene un valor del caché si no expiró */
  get<T>(key: string): T | undefined {
    this.cleanExpired();
    const entry = this.cache.get(key);
    if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
      return entry.data as T;
    }
    return undefined;
  }

  /** Guarda un valor en el caché */
  set<T>(key: string, value: T): void {
    this.cache.set(key, { data: value, timestamp: Date.now() });
  }

  /**
   * Obtiene un valor del caché o lo obtiene con fetcher y lo guarda.
   * @param key Clave única
   * @param fetcher Función que retorna una promesa del dato
   */
  async getOrSet<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const cached = this.get<T>(key);
    if (cached !== undefined) {
      return cached;
    }
    const data = await fetcher();
    this.set(key, data);
    return data;
  }

  /** Limpia el caché completamente */
  clear() {
    this.cache.clear();
  }

  /** Limpia solo los valores expirados */
  cleanExpired() {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp >= CACHE_TTL) {
        this.cache.delete(key);
      }
    }
  }
}

// Exportar una única instancia global
const globalCache = new GlobalCache();
export default globalCache;
